import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Download,
  Trash,
  MoreHorizontal,
  FileText,
  Loader2,
  X,
  ArrowUp,
  ArrowDown,
  RotateCw,
  Check,
  ChevronRight,
  FolderInput,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  useDocumentsStore,
  type UploadingFile,
  type DocumentSortKey,
  type SortDirection,
} from '@/stores/documents-store'
import { useReconciliationStore } from '@/stores/reconciliation-store'
import type {
  Document,
  DocumentCategory,
  DocumentDisplayStatus,
  ReconciliationRunSummary,
} from '@/lib/schemas'
import {
  BID_DOCUMENT_CATEGORY_LABELS,
  DOCUMENT_CATEGORY_LABELS,
  DOCUMENT_CATEGORY_COLORS,
  DOCUMENT_DISPLAY_STATUS_CONFIG,
  getDocumentDisplayStatus,
  formatFileSize,
  isAddendumDocument,
  addendumRoleSource,
  isBaselineAddendum,
} from '@/lib/schemas'
import { FileTypeIcon } from '@/components/ui/file-type-icon'
import { GradientSparkleIcon } from '@/components/extraction/GradientSparkleIcon'
import { cn } from '@/lib/utils'
import { toastApiError } from '@/lib/api'
import { usePreviewStore } from '@/stores/preview-store'
import { DocumentSchedulesLink } from '@/components/word-form/DocumentSchedulesLink'
import { isWordDocument } from '@/lib/word-form'
import { useWordFormStore } from '@/stores/word-form-store'
import {
  BID_MOVE_TO_CATEGORIES,
  analysisMembershipNote,
  bidDocumentCategory,
  bidDocumentCategoryLabel,
  groupBidDocuments,
  humaniseCategory,
} from '@/lib/bid-document-groups'

/** Groups that start open whatever the pack's size (spec 25 §4.2). */
const GROUPS_OPEN_BY_DEFAULT: ReadonlySet<DocumentCategory> = new Set(['rfp_tender', 'addenda'])
/** At or under this many rows every group starts open — nothing to tidy away. */
const ALL_GROUPS_OPEN_MAX_ROWS = 15

/**
 * Grouped-mode responsive columns. The bid Documents tab must stay usable from
 * phone width up, so the name keeps its room by shedding columns instead of
 * squeezing to nothing. Breakpoints are viewport widths, but from md up the
 * app sidebar takes 192px (expanded), so the list is ~240px narrower than the
 * breakpoint name suggests; every step below is sized for that.
 *  - Status sits under the name below sm (640px): a phone has no room for a
 *    readable name and a status column side by side.
 *  - Size shows from md (768px), Date from lg (1024px).
 *  - The actions cell narrows: below lg the hover-only Download moves to the
 *    menu only; below md the inline Retry does too, leaving just the menu.
 *  - The reconcile control (addenda only) gets its 176px column from xl
 *    (1280px) and drops to its own line below — at lg, beside Date and the
 *    wide actions cell, it left the name no room at all.
 *  - Chips beside the name ("Looks like an addendum", "Suggested") drop under
 *    it whenever the name would go below 8rem.
 * Flat mode keeps its fixed columns.
 */
const GROUPED_ACTIONS_WIDTH = 'w-7 md:w-16 lg:w-24'

interface DocumentListProps {
  documents: Document[]
  uploadingFiles?: UploadingFile[]
  isLoading: boolean
  readOnly?: boolean
  /**
   * When true, the list pulls selection + sort state from the documents store
   * and renders checkboxes + sortable headers. Default false keeps the simple
   * presentational mode used by older callers.
   */
  selectable?: boolean
  /**
   * The bid Documents tab layout (pack map PR3). Rows are grouped under
   * collapsible headings by their effective category, in the tender pack's
   * canonical order, and the Category column is dropped — the heading does its
   * job. Rows gain the one-line summary, the "Suggested" label and the
   * analysis-membership note; addendum rows get detection chips + reconcile
   * actions; the kebab gains Move to / Keep here and the Treat/Not-an-addendum
   * toggle. Group counts are the rows passed in, so they follow the filter.
   * Requires `bidId`.
   */
  grouped?: boolean
  /** Bid the list belongs to — needed to look up + start reconciliation runs. */
  bidId?: string
}

export function DocumentList({
  documents,
  uploadingFiles = [],
  isLoading,
  readOnly = false,
  selectable = false,
  grouped = false,
  bidId,
}: DocumentListProps) {
  const store = useDocumentsStore()
  const navigate = useNavigate()
  const { downloadDocument, deleteDocument, removeUploadingFile } = store
  const setDocumentAddendumRole = store.setDocumentAddendumRole ?? (async () => {})
  const retryDocument = store.retryDocument ?? (async () => {})
  const setDocumentCategory = store.setDocumentCategory ?? (async () => {})

  const reconRuns = useReconciliationStore((s) => (bidId ? s.runsByBid[bidId] : undefined))
  const startReconcileRun = useReconciliationStore((s) => s.startRun)

  // "N schedules found" (R3): ONE call for the whole list, never one per row.
  // Refetched when a Word file arrives or finishes processing — discovery
  // runs in the background after upload.
  const loadBidSchedules = useWordFormStore((s) => s.loadBidSchedules)
  const wordDocumentsKey =
    grouped && bidId
      ? documents
          .filter((d) => isWordDocument(d.mimeType))
          .map((d) => `${d.id}:${d.processingStatus}`)
          .sort()
          .join(',')
      : ''
  const loadedWordDocumentsKey = useRef<string | null>(null)
  useEffect(() => {
    if (!bidId || !wordDocumentsKey) return
    const isChange =
      loadedWordDocumentsKey.current !== null && loadedWordDocumentsKey.current !== wordDocumentsKey
    loadedWordDocumentsKey.current = wordDocumentsKey
    void loadBidSchedules(bidId, isChange)
  }, [bidId, wordDocumentsKey, loadBidSchedules])

  const runForDocument = (documentId: string): ReconciliationRunSummary | null => {
    const forDoc = (reconRuns ?? []).filter((r) => r.documentId === documentId)
    if (forDoc.length === 0) return null
    return forDoc.reduce((latest, r) => (r.createdAt > latest.createdAt ? r : latest))
  }

  const handleStartReconcile = async (documentId: string) => {
    if (!bidId) return
    try {
      await startReconcileRun(bidId, documentId)
    } catch (error) {
      toastApiError(error, 'Failed to start reconciliation')
    }
  }

  // Open the Analysis-tab review surface for a finished/finishing run.
  const handleViewReview = (run: ReconciliationRunSummary) => {
    if (!bidId) return
    void navigate(`/app/bids/${bidId}/qualify?view=addendum-review&run=${run.id}`)
  }

  const handleSetAddendumRole = async (documentId: string, role: 'addendum' | 'none') => {
    try {
      await setDocumentAddendumRole(documentId, role)
    } catch (error) {
      toastApiError(error, 'Failed to update document')
    }
  }

  // "Move to" and "Keep here". The store moves the row at once and puts it
  // back if the server refuses (e.g. 409 on an addendum); say why.
  const handleSetCategory = async (documentId: string, category: DocumentCategory) => {
    try {
      await setDocumentCategory(documentId, category)
    } catch (error) {
      toastApiError(error, 'Failed to move document')
    }
  }
  const selectedDocumentIds = store.selectedDocumentIds ?? new Set<string>()
  const toggleSelection = store.toggleSelection ?? (() => {})
  const selectAllDocuments = store.selectAllDocuments ?? (() => {})
  const clearSelection = store.clearSelection ?? (() => {})
  const sortKey: DocumentSortKey = store.sortKey ?? 'date'
  const sortDir: SortDirection = store.sortDir ?? 'desc'
  const setSort = store.setSort ?? (() => {})

  const [deleteConfirm, setDeleteConfirm] = useState<Document | null>(null)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [retryingId, setRetryingId] = useState<string | null>(null)
  // Only the groups the user has opened or closed; the rest use the default.
  const [groupOpenOverrides, setGroupOpenOverrides] = useState<
    Partial<Record<DocumentCategory, boolean>>
  >({})

  const handleDownload = async (doc: Document) => {
    try {
      setDownloadingId(doc.id)
      await downloadDocument(doc.id)
    } finally {
      setDownloadingId(null)
    }
  }

  const handleDelete = async () => {
    if (!deleteConfirm) return
    try {
      setDeletingId(deleteConfirm.id)
      await deleteDocument(deleteConfirm.id)
      setDeleteConfirm(null)
    } finally {
      setDeletingId(null)
    }
  }

  const handleRetry = async (doc: Document) => {
    try {
      setRetryingId(doc.id)
      await retryDocument(doc.id)
    } catch (error) {
      toastApiError(error, 'Failed to retry document')
    } finally {
      setRetryingId(null)
    }
  }

  // Grouped mode: one group per effective category, canonical order, empty
  // groups omitted. Each group keeps the upstream sort; the filter and search
  // have already been applied, so the counts are the visible rows.
  const groups = grouped ? groupBidDocuments(documents) : []
  const renderedDocs = documents
  const activeAddenda = grouped ? documents.filter(isAddendumDocument) : []

  const isGroupOpen = (category: DocumentCategory): boolean =>
    groupOpenOverrides[category] ??
    (documents.length <= ALL_GROUPS_OPEN_MAX_ROWS || GROUPS_OPEN_BY_DEFAULT.has(category))
  const toggleGroup = (category: DocumentCategory) => {
    const next = !isGroupOpen(category)
    setGroupOpenOverrides((prev) => ({ ...prev, [category]: next }))
  }

  // Every addendum here predates the bid's first analysis, so the group offers
  // no reconcile actions at all. Say why, or the missing button reads as the
  // addenda having been ignored.
  const allAddendaAreBaseline =
    activeAddenda.length > 0 && activeAddenda.every(isBaselineAddendum)

  // The reconcile column exists for the whole list or not at all — a per-row
  // slot would shift every other column on the rows that lack it.
  const showReconcileSlot = activeAddenda.length > 0

  // Header checkbox state (indeterminate when partially selected)
  const allDocIds = renderedDocs.map((d) => d.id)
  const selectedVisibleCount = allDocIds.filter((id) =>
    selectedDocumentIds.has(id)
  ).length
  const headerCheckedState: boolean | 'indeterminate' =
    renderedDocs.length === 0
      ? false
      : selectedVisibleCount === renderedDocs.length
        ? true
        : selectedVisibleCount > 0
          ? 'indeterminate'
          : false

  const handleHeaderToggle = (checked: boolean | 'indeterminate') => {
    if (checked === true) {
      selectAllDocuments(allDocIds)
    } else {
      clearSelection()
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--muted-foreground))]" />
      </div>
    )
  }

  if (renderedDocs.length === 0 && uploadingFiles.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FileText className="h-8 w-8 text-[hsl(var(--muted-foreground))]" />
        <h3 className="mt-4 text-base font-medium">No documents yet</h3>
        <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
          Upload Tender/RFP documents to get started with AI-powered analysis.
        </p>
        <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">
          Supported: PDF, Word, Excel, PowerPoint, CSV, TXT — or a ZIP or folder
        </p>
      </div>
    )
  }

  return (
    <>
      <div>
        {/* Table Header */}
        <div className="flex items-center gap-3 px-3 py-3 text-xs font-medium text-muted-foreground border-b bg-muted/30">
          {selectable && (
            <div className="w-6 flex items-center">
              <Checkbox
                checked={headerCheckedState}
                onCheckedChange={handleHeaderToggle}
                aria-label={
                  headerCheckedState === true
                    ? 'Deselect all documents'
                    : 'Select all documents'
                }
                disabled={readOnly}
              />
            </div>
          )}
          <div className="flex-1">
            {selectable ? (
              <SortHeader
                label="Name"
                columnKey="name"
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={setSort}
              />
            ) : (
              'Name'
            )}
          </div>
          {!grouped && <div className="w-32">Category</div>}
          <div className={cn('w-24', grouped && 'hidden sm:block')}>Status</div>
          <div className={cn('w-16 text-right', grouped && 'hidden md:block')}>
            {selectable ? (
              <SortHeader
                label="Size"
                columnKey="size"
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={setSort}
                align="end"
              />
            ) : (
              'Size'
            )}
          </div>
          <div className={cn('w-24 text-right', grouped && 'hidden lg:block')}>
            {selectable ? (
              <SortHeader
                label="Date"
                columnKey="date"
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={setSort}
                align="end"
              />
            ) : (
              'Date'
            )}
          </div>
          {/* Below xl the reconcile control drops to its own line under the
              name, so the column (and its header) exists only from xl up. */}
          {showReconcileSlot && <div className="hidden xl:block w-44" />}
          <div className={grouped ? GROUPED_ACTIONS_WIDTH : 'w-24'} />
        </div>

        {/* Uploading files */}
        {uploadingFiles.length > 0 && (
          <div className="space-y-0.5 mb-1">
            {uploadingFiles.map((file) => (
              <UploadingFileItem
                key={file.id}
                file={file}
                selectable={selectable}
                grouped={grouped}
                onRemove={() => removeUploadingFile(file.id)}
              />
            ))}
          </div>
        )}

        {/* Document rows */}
        {grouped ? (
          <div>
            {groups.map(({ category, documents: groupDocs }) => {
              const isOpen = isGroupOpen(category)
              return (
                <section key={category} aria-label={bidDocumentCategoryLabel(category)}>
                  <GroupHeader
                    label={bidDocumentCategoryLabel(category)}
                    count={groupDocs.length}
                    isOpen={isOpen}
                    onToggle={() => toggleGroup(category)}
                  />
                  {isOpen && category === 'addenda' && allAddendaAreBaseline && (
                    <p className="px-3 pt-1.5 text-meta">
                      Uploaded before this bid was analysed — they'll be read as part of the
                      analysis, not as changes to it.
                    </p>
                  )}
                  {isOpen && (
                    <div className="space-y-0.5">
                      {groupDocs.map((doc) => {
                        const isAddendum = isAddendumDocument(doc)
                        return (
                          <DocumentItem
                            key={doc.id}
                            document={doc}
                            selectable={selectable}
                            isSelected={selectedDocumentIds.has(doc.id)}
                            onToggleSelect={() => toggleSelection(doc.id)}
                            onDownload={() => void handleDownload(doc)}
                            onDelete={() => setDeleteConfirm(doc)}
                            isDownloading={downloadingId === doc.id}
                            onRetry={() => void handleRetry(doc)}
                            isRetrying={retryingId === doc.id}
                            readOnly={readOnly}
                            grouped
                            isAddendum={isAddendum}
                            showAddendumActions
                            showReconcileSlot={showReconcileSlot}
                            run={isAddendum ? runForDocument(doc.id) : null}
                            onStartReconcile={() => handleStartReconcile(doc.id)}
                            onSetAddendumRole={(role) => void handleSetAddendumRole(doc.id, role)}
                            onSetCategory={(next) => void handleSetCategory(doc.id, next)}
                            onViewReview={handleViewReview}
                            scheduleLink={
                              bidId && isWordDocument(doc.mimeType) ? (
                                <DocumentSchedulesLink
                                  bidId={bidId}
                                  documentId={doc.id}
                                  documentName={doc.name}
                                  readOnly={readOnly}
                                />
                              ) : null
                            }
                          />
                        )
                      })}
                    </div>
                  )}
                </section>
              )
            })}
          </div>
        ) : (
          <div className="space-y-0.5">
            {documents.map((doc) => (
              <DocumentItem
                key={doc.id}
                document={doc}
                selectable={selectable}
                isSelected={selectedDocumentIds.has(doc.id)}
                onToggleSelect={() => toggleSelection(doc.id)}
                onDownload={() => void handleDownload(doc)}
                onDelete={() => setDeleteConfirm(doc)}
                isDownloading={downloadingId === doc.id}
                onRetry={() => void handleRetry(doc)}
                isRetrying={retryingId === doc.id}
                readOnly={readOnly}
              />
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteConfirm?.name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deletingId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDelete()}
              disabled={!!deletingId}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingId ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

// ===================
// Sort Header Button
// ===================

interface SortHeaderProps {
  label: string
  columnKey: DocumentSortKey
  sortKey: DocumentSortKey
  sortDir: SortDirection
  onSort: (key: DocumentSortKey) => void
  align?: 'start' | 'end'
}

function SortHeader({
  label,
  columnKey,
  sortKey,
  sortDir,
  onSort,
  align = 'start',
}: SortHeaderProps) {
  const isActive = sortKey === columnKey
  return (
    <button
      type="button"
      onClick={() => onSort(columnKey)}
      className={cn(
        'inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors',
        align === 'end' && 'justify-end'
      )}
      aria-sort={isActive ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span>{label}</span>
      {isActive ? (
        sortDir === 'asc' ? (
          <ArrowUp className="h-3 w-3" />
        ) : (
          <ArrowDown className="h-3 w-3" />
        )
      ) : null}
    </button>
  )
}

// ===================
// Processing Status Badge Component
// ===================

/**
 * Human-readable reason a document is in the failed state. Indexing failures
 * are written by markIndexingFailed as `{ error: { code, message } }` on
 * indexing_metadata; processing failures by markProcessingFailed on
 * processing_metadata. Returns null when neither is stored — e.g. list
 * endpoints that omit the jsonb blobs. (WF1)
 */
function getFailureReason(document: Document): string | null {
  const indexingMessage = (document.indexingMetadata as { error?: { message?: unknown } } | null | undefined)?.error?.message
  const processingMessage = (document.processingMetadata as { error?: { message?: unknown } } | null | undefined)?.error?.message
  // Prefer the stage that actually failed. A retried index-failure that then
  // fails at extraction still carries the old indexing_metadata, so reading
  // indexing first would show a stale reason for a fresh extraction failure.
  const [first, second] =
    document.processingStatus === 'failed'
      ? [processingMessage, indexingMessage]
      : [indexingMessage, processingMessage]
  if (typeof first === 'string' && first.length > 0) return first
  return typeof second === 'string' && second.length > 0 ? second : null
}

const MAX_DOCUMENT_RETRIES = 3

/**
 * Why a failed document cannot be retried, or null when it can. Mirrors the
 * server's checks in handleRetryProcessing so the button is honest before the
 * click; when metadata is missing we show the button and let the server decide.
 */
function getRetryBlocker(document: Document): string | null {
  if (document.processingStatus !== 'failed') return null // index-failed → always retryable
  const meta = document.processingMetadata as
    | { error?: { retryable?: unknown }; retryCount?: unknown }
    | null
    | undefined
  if (meta?.error?.retryable === false) return "This failure can't be retried — upload the file again"
  if (typeof meta?.retryCount === 'number' && meta.retryCount >= MAX_DOCUMENT_RETRIES) {
    return 'Retry limit reached — upload the file again'
  }
  return null
}

interface ProcessingStatusBadgeProps {
  status: DocumentDisplayStatus
  /** Human-readable failure reason, surfaced as a tooltip on a failed badge (WF1). */
  failureReason?: string | null
}

function ProcessingStatusBadge({ status, failureReason }: ProcessingStatusBadgeProps) {
  const config = DOCUMENT_DISPLAY_STATUS_CONFIG[status]
  const isSpinning = status === 'processing'

  return (
    <Badge
      variant={config.variant}
      dot={isSpinning ? false : undefined}
      className="text-xs"
      title={status === 'failed' && failureReason ? failureReason : undefined}
    >
      {isSpinning && <Loader2 className="h-3 w-3 animate-spin" />}
      {config.label}
    </Badge>
  )
}

// ===================
// Addenda group + reconciliation helpers
// ===================

/** Flat, collapsible group heading (bg-muted/50) for the grouped bid list. */
function GroupHeader({
  label,
  count,
  isOpen,
  onToggle,
}: {
  label: string
  count: number
  isOpen: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      className="flex w-full items-center gap-1.5 px-3 py-1.5 mt-1 bg-muted/50 text-left text-xs font-medium text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <ChevronRight
        className={cn('h-3.5 w-3.5 shrink-0 text-subtle transition-transform', isOpen && 'rotate-90')}
        aria-hidden
      />
      <span>{label}</span>{' '}
      <span className="tabular-nums">({count})</span>
    </button>
  )
}

/**
 * Quiet marker for a row grouped by an unconfirmed detected type. "Keep here"
 * or "Move to" makes the placement the user's and removes it.
 */
function SuggestedLabel() {
  return (
    <Badge
      variant="outline"
      dot={false}
      className="max-w-full min-w-0 px-2 text-[10px] font-normal text-muted-foreground whitespace-nowrap"
      title="Placed here from what the file looks like. Use Keep here or Move to to confirm."
    >
      <span className="truncate">Suggested</span>
    </Badge>
  )
}

/**
 * Detection/confirmation chip beside an addendum row's name. It may shrink and
 * truncate, so a narrow name cell never pushes it over the Status column.
 */
function AddendumTypePill({ source }: { source: 'user' | 'detected' }) {
  return (
    <Badge
      dot={false}
      className="max-w-full min-w-0 text-[10px] whitespace-nowrap border-[hsl(var(--ai-accent)/0.35)] bg-[hsl(var(--ai-accent-soft))] text-[hsl(var(--ai-accent-text))]"
    >
      <span className="truncate">{source === 'user' ? 'Addendum' : 'Looks like an addendum'}</span>
    </Badge>
  )
}

/**
 * Run-status pill for an addendum row. Terminal review states (`review`,
 * `applied`, `dismissed`) become buttons that open the Analysis-tab review
 * surface when `onView` is supplied; queued/running/failed stay non-interactive.
 */
function ReconciliationStatusPill({
  run,
  onView,
}: {
  run: ReconciliationRunSummary
  onView?: (() => void) | undefined
}) {
  switch (run.status) {
    case 'queued':
      return (
        <Badge variant="secondary" dot={false} className="text-[10px]">
          Queued
        </Badge>
      )
    case 'running':
      return (
        <Badge variant="warning" dot={false} className="text-[10px]">
          <Loader2 className="h-3 w-3 animate-spin" />
          Analysing…
        </Badge>
      )
    case 'review': {
      const n = run.decisionCounts.pending
      const label = `${n} change${n === 1 ? '' : 's'} pending review`
      const pill = (
        <Badge variant="warning" className="text-[10px] whitespace-nowrap">
          {label}
        </Badge>
      )
      return onView ? (
        <button
          type="button"
          onClick={onView}
          aria-label={`Review ${label}`}
          className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:opacity-80 transition-opacity"
        >
          {pill}
        </button>
      ) : (
        pill
      )
    }
    case 'applied':
    case 'dismissed': {
      const n = run.decisionCounts.accepted
      const label = `Reviewed · ${n} accepted`
      const pill = (
        <Badge variant="success" className="text-[10px] whitespace-nowrap">
          {label}
        </Badge>
      )
      return onView ? (
        <button
          type="button"
          onClick={onView}
          aria-label={`View reconciliation — ${label}`}
          className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:opacity-80 transition-opacity"
        >
          {pill}
        </button>
      ) : (
        pill
      )
    }
    case 'failed':
      return (
        <Badge
          variant="destructive"
          dot={false}
          className="text-[10px] whitespace-nowrap"
          title={run.error ?? undefined}
        >
          Analysis failed
        </Badge>
      )
    default:
      return null
  }
}

// ===================
// Document Item Component
// ===================

interface DocumentItemProps {
  document: Document
  selectable: boolean
  isSelected: boolean
  onToggleSelect: () => void
  onDownload: () => void
  onDelete: () => void
  isDownloading: boolean
  onRetry?: () => void
  isRetrying?: boolean
  readOnly: boolean
  /**
   * Bid Documents tab row: no Category cell (the group heading carries it),
   * the summary line and quiet labels under/beside the name.
   */
  grouped?: boolean
  /** Grouped mode adds a Treat/Not-an-addendum toggle to every row's kebab. */
  showAddendumActions?: boolean
  /** Renders the addendum row variant (detection chip in the Category cell). */
  isAddendum?: boolean
  /**
   * Reserves the reconcile-action column on EVERY row in the list, so the
   * control lines up. Set for the whole list when it contains any addenda.
   */
  showReconcileSlot?: boolean
  /** Latest reconciliation run for this document, if any. */
  run?: ReconciliationRunSummary | null
  onStartReconcile?: () => void | Promise<void>
  onSetAddendumRole?: (role: 'addendum' | 'none') => void
  /** Move to / Keep here. Omitted outside the grouped bid list. */
  onSetCategory?: (category: DocumentCategory) => void
  /** Opens the Analysis-tab review surface for this row's run. */
  onViewReview?: (run: ReconciliationRunSummary) => void
  /** Grouped mode: "6 schedules found" under a Word tender (R3). */
  scheduleLink?: ReactNode
}

function DocumentItem({
  document,
  selectable,
  isSelected,
  onToggleSelect,
  onDownload,
  onDelete,
  isDownloading,
  onRetry,
  isRetrying = false,
  readOnly,
  grouped = false,
  showAddendumActions = false,
  isAddendum = false,
  showReconcileSlot = false,
  run = null,
  onStartReconcile,
  onSetAddendumRole,
  onSetCategory,
  onViewReview,
  scheduleLink,
}: DocumentItemProps) {
  const [isStarting, setIsStarting] = useState(false)
  const categoryLabel = DOCUMENT_CATEGORY_LABELS[document.type] ?? humaniseCategory(document.type)
  const categoryColor = DOCUMENT_CATEGORY_COLORS[document.type] || 'bg-gray-100 text-gray-800'
  const displayStatus = getDocumentDisplayStatus(document)
  const retryBlocker = getRetryBlocker(document)
  const showRetry = displayStatus === 'failed' && !readOnly && !!onRetry

  // Bid-row extras (grouped mode only).
  const currentCategory = bidDocumentCategory(document)
  const isSuggested = grouped && !isAddendum && !!document.suggestedCategory
  const summary = grouped ? document.aiSummary?.trim() || null : null
  const membershipNote = grouped ? analysisMembershipNote(document) : null
  // An active addendum stays in Addenda: the server refuses a category move
  // (409), and "Not an addendum" is the way out.
  const canMove = grouped && !readOnly && !isAddendum && !!onSetCategory

  const createdDate = new Date(document.createdAt).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })

  // A baseline addendum (uploaded before the bid had any analysis) is ordinary
  // source material — it keeps its chip but offers no reconcile action, because
  // there is no earlier analysis for it to be a change to.
  const isBaseline = isBaselineAddendum(document)
  const canReconcile = isAddendum && !isBaseline

  // Review-changes button shows when there's no run yet or the last run failed —
  // i.e. the addendum hasn't been (successfully) reconciled. readOnly hides it.
  const showReviewButton =
    canReconcile && !readOnly && (!run || run.status === 'failed')
  const showSpinnerPill = canReconcile && (isStarting || run?.status === 'running')
  const hasReconcileContent = showSpinnerPill || showReviewButton || (canReconcile && !!run)

  const handleOpenPreview = () =>
    void usePreviewStore.getState().openPreview({
      documentId: document.id,
      documentName: document.name,
      context: 'library',
    })

  const handleReviewClick = async () => {
    if (!onStartReconcile) return
    setIsStarting(true)
    try {
      await Promise.resolve(onStartReconcile())
    } finally {
      setIsStarting(false)
    }
  }

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent/50 transition-colors group',
        grouped && 'max-xl:flex-wrap gap-y-1',
        isSelected && 'bg-accent/40'
      )}
    >
      {selectable && (
        <div className="w-6 flex items-center">
          <Checkbox
            checked={isSelected}
            onCheckedChange={onToggleSelect}
            aria-label={`Select ${document.name}`}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Icon & Name */}
      {grouped ? (
        <div className="flex-1 flex items-center gap-2 min-w-0">
          <FileTypeIcon mimeType={document.mimeType} size={20} />
          <div className="flex-1 min-w-0">
            {/* The name starts at 8rem (basis-32) for line breaking, then grows
                to its own width (max-w-fit). So a chip sits beside it only
                while the name keeps at least 8rem; otherwise the chip wraps to
                the next line and the name takes the whole line. */}
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 min-w-0">
              <button
                className="grow basis-32 max-w-fit min-w-0 text-sm truncate text-left hover:text-[hsl(var(--brand))] transition-colors"
                onClick={handleOpenPreview}
              >
                {document.name}
              </button>
              {/* The group heading carries the category, so an addendum's chip
                  and the "Suggested" label sit beside the name instead. */}
              {isAddendum && <AddendumTypePill source={addendumRoleSource(document)} />}
              {isSuggested && <SuggestedLabel />}
            </div>
            {summary && (
              <p className="text-meta truncate min-w-0" title={summary}>
                {summary}
              </p>
            )}
            {/* Its own line, so it truncates inside the name cell instead of
                spilling over the Status column when the cell is narrow. */}
            {membershipNote && (
              <p
                className="text-meta truncate min-w-0"
                title={membershipNote}
                data-testid="document-membership-note"
              >
                {membershipNote}
              </p>
            )}
            {scheduleLink}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center gap-2 min-w-0">
          <FileTypeIcon mimeType={document.mimeType} size={20} />
          <button
            className="text-sm truncate text-left hover:text-[hsl(var(--brand))] transition-colors max-w-full block"
            onClick={handleOpenPreview}
          >
            {document.name}
          </button>
        </div>
      )}

      {/* Category. An addendum's chip IS its category, so it takes this cell
          rather than displacing the row's other columns — which is what used to
          leave addendum rows with no status, size or date at all. */}
      {!grouped && (
        <div className="w-32">
          {isAddendum ? (
            <AddendumTypePill source={addendumRoleSource(document)} />
          ) : (
            <Badge variant="secondary" className={cn('text-[10px] whitespace-nowrap', categoryColor)}>
              {categoryLabel}
            </Badge>
          )}
        </div>
      )}

      {/* Status. On a phone it takes its own line under the name, lined up
          with the name text, so the name can use the full row. */}
      <div
        className={cn(
          'w-24',
          grouped && 'max-sm:order-last max-sm:basis-full',
          grouped && (selectable ? 'max-sm:pl-16' : 'max-sm:pl-7')
        )}
      >
        <ProcessingStatusBadge status={displayStatus} failureReason={getFailureReason(document)} />
      </div>

      {/* Size */}
      <div className={cn('w-16 text-meta text-right', grouped && 'hidden md:block')}>
        {formatFileSize(document.size)}
      </div>

      {/* Date */}
      <div className={cn('w-24 text-meta text-right', grouped && 'hidden lg:block')}>
        {createdDate}
      </div>

      {/* Reconcile action / status. Its own fixed slot so the control lines up
          down the column instead of shifting with each row's content. Rendered
          only when the list actually holds addenda, so the Library view — which
          never has any — keeps its current width. */}
      {showReconcileSlot && (
        <div
          className={cn(
            // Wraps so a failed run's pill + retry button (~226px) stack inside
            // the 176px column rather than spilling over Date.
            'shrink-0 flex flex-wrap items-center gap-2',
            'xl:w-44 xl:justify-end',
            // Below xl: its own line under the name, aligned with the name text.
            'max-xl:order-last max-xl:basis-full max-xl:justify-start',
            selectable ? 'max-xl:pl-16' : 'max-xl:pl-7',
            !hasReconcileContent && 'max-xl:hidden'
          )}
        >
          {showSpinnerPill ? (
            <Badge variant="warning" dot={false} className="text-[10px]">
              <Loader2 className="h-3 w-3 animate-spin" />
              Analysing…
            </Badge>
          ) : (
            <>
              {/* A failed run shows its status pill next to the retry button. */}
              {canReconcile && run?.status === 'failed' && <ReconciliationStatusPill run={run} />}
              {showReviewButton ? (
                <Button
                  variant="ai-outline"
                  size="sm"
                  onClick={() => void handleReviewClick()}
                  disabled={isStarting}
                >
                  <GradientSparkleIcon className="h-3.5 w-3.5 mr-1" />
                  Review changes
                </Button>
              ) : canReconcile && run && run.status !== 'failed' ? (
                <ReconciliationStatusPill
                  run={run}
                  onView={onViewReview ? () => onViewReview(run) : undefined}
                />
              ) : null}
            </>
          )}
        </div>
      )}

      {/* Actions */}
      <div
        className={cn(
          'flex items-center justify-end gap-1',
          grouped ? GROUPED_ACTIONS_WIDTH : 'w-24'
        )}
      >
        {showRetry && (
          <Button
            variant="ghost"
            size="icon"
            className={cn('h-7 w-7', grouped && 'hidden md:inline-flex')}
            onClick={onRetry}
            disabled={isRetrying || retryBlocker !== null}
            aria-label={`Retry processing ${document.name}`}
            title={retryBlocker ?? 'Retry processing'}
          >
            {isRetrying ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RotateCw className="h-4 w-4" />
            )}
          </Button>
        )}

        <Button
          variant="ghost"
          size="icon"
          className={cn(
            'h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity',
            grouped && 'hidden lg:inline-flex'
          )}
          onClick={onDownload}
          disabled={isDownloading}
        >
          {isDownloading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity',
                // Touch screens have no hover: keep the menu visible on a phone.
                grouped && 'max-md:opacity-100'
              )}
              disabled={readOnly}
              aria-label="Document actions"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onDownload}>
              <Download className="mr-2 h-4 w-4" />
              Download
            </DropdownMenuItem>
            {showRetry && (
              <DropdownMenuItem onClick={onRetry} disabled={isRetrying || retryBlocker !== null}>
                <RotateCw className="mr-2 h-4 w-4" />
                Retry processing
              </DropdownMenuItem>
            )}
            {canMove && onSetCategory && (
              <>
                <DropdownMenuSeparator />
                {isSuggested && (
                  <DropdownMenuItem onClick={() => onSetCategory(currentCategory)}>
                    <Check className="mr-2 h-4 w-4" />
                    Keep here
                  </DropdownMenuItem>
                )}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <FolderInput className="mr-2 h-4 w-4" />
                    Move to
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-56">
                    {BID_MOVE_TO_CATEGORIES.filter((c) => c !== currentCategory).map((category) => (
                      <DropdownMenuItem key={category} onClick={() => onSetCategory(category)}>
                        {BID_DOCUMENT_CATEGORY_LABELS[category]}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              </>
            )}
            {!readOnly && showAddendumActions && onSetAddendumRole && (
              <>
                {!canMove && <DropdownMenuSeparator />}
                <DropdownMenuItem
                  onClick={() => onSetAddendumRole(isAddendum ? 'none' : 'addendum')}
                >
                  {isAddendum ? (
                    <>
                      <FileText className="mr-2 h-4 w-4" />
                      Not an addendum
                    </>
                  ) : (
                    <>
                      <GradientSparkleIcon className="mr-2 h-4 w-4" />
                      Treat as addendum
                    </>
                  )}
                </DropdownMenuItem>
              </>
            )}
            {!readOnly && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={onDelete}
                  className="text-muted-foreground focus:text-destructive focus:bg-destructive/10"
                >
                  <Trash className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}

// ===================
// Uploading File Item Component
// ===================

interface UploadingFileItemProps {
  file: UploadingFile
  selectable: boolean
  /** The grouped bid list: no Category column, and the responsive columns. */
  grouped?: boolean
  onRemove: () => void
}

function UploadingFileItem({
  file,
  selectable,
  grouped = false,
  onRemove,
}: UploadingFileItemProps) {
  const isError = file.status === 'error'
  const isUploading = file.status === 'uploading' || file.status === 'pending'

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-md transition-colors',
        grouped && 'max-sm:flex-wrap gap-y-1',
        isError
          ? 'bg-destructive/5 border border-destructive/20'
          : 'bg-muted/30'
      )}
    >
      {selectable && <div className="w-6" />}

      {/* Icon & Name */}
      <div className="flex-1 flex items-center gap-2 min-w-0">
        <FileTypeIcon mimeType={file.file.type} size={20} />
        <div className="min-w-0">
          <p className="text-sm truncate">{file.file.name}</p>
          {isError && file.error && (
            <p className="text-xs text-destructive truncate">{file.error}</p>
          )}
        </div>
      </div>

      {/* Category placeholder */}
      {!grouped && <div className="w-32" />}

      {/* Status — under the name on a phone, as on document rows. */}
      <div
        className={cn(
          'w-24',
          grouped && 'max-sm:order-last max-sm:basis-full',
          grouped && (selectable ? 'max-sm:pl-16' : 'max-sm:pl-7'),
          grouped && !isUploading && !isError && 'max-sm:hidden'
        )}
      >
        {isUploading && (
          <Badge variant="warning" dot={false} className="text-[10px]">
            <Loader2 className="h-3 w-3 animate-spin" />
            {file.progress > 0 ? `${file.progress}%` : 'Uploading'}
          </Badge>
        )}
        {isError && (
          <Badge variant="destructive" className="text-[10px]">
            Failed
          </Badge>
        )}
      </div>

      {/* Size */}
      <div className={cn('w-16 text-meta text-right', grouped && 'hidden md:block')}>
        {formatFileSize(file.file.size)}
      </div>

      {/* Date placeholder */}
      <div className={cn('w-24', grouped && 'hidden lg:block')} />

      {/* Actions - remove button for errors */}
      <div
        className={cn('flex items-center justify-end', grouped ? GROUPED_ACTIONS_WIDTH : 'w-24')}
      >
        {isError && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={onRemove}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  )
}
